import { SecureContentsId } from 'shared-client/secure-contents-id';
import { encryptContents } from 'shared-client/crypto';
import { serBlob, serDEK } from 'shared-client/serde';

export type SecureContentsUpload = {
  secureContentsId: string;
  encryptedBlob: Blob;
  encryptedDek: Uint8Array<ArrayBuffer>;
  fileContentType: string;
  fileName: string;
  fileSize: number;
};

export class SecureContents {
  constructor(
    readonly secureContentsId: SecureContentsId,
    readonly dek: CryptoKey,
  ) {}

  static async fromRandom(wordList: string[]) {
    const id = await SecureContentsId.fromRandomBytes(wordList);
    const dek = await generateRandomDEK();
    return new SecureContents(id, dek);
  }

  async upload(file: File): Promise<SecureContentsUpload> {
    const encryptedFile = await encryptContents(this.dek, await serBlob(file));
    const encryptedDEK = await encryptContents(
      await this.secureContentsId.getDEK(),
      await serDEK(this.dek),
    );
    return {
      secureContentsId: await this.secureContentsId.getIdHash(),
      encryptedBlob: new Blob(
        [encryptedFile],
        { type: file.type },
      ),
      encryptedDek: encryptedDEK,
      fileContentType: file.type,
      fileName: file.name,
      fileSize: file.size,
    };
  }
}

async function generateRandomDEK(): Promise<CryptoKey> {
  const extractable = true;
  const key = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256, // 256 bits
    },
    extractable,
    ['encrypt', 'decrypt'],
  );

  if (!key || !(key instanceof CryptoKey)) {
    throw new Error('Failed to generate DEK');
  }
  return key;
}
